import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { reportsAPI } from '../../lib/api';
import { fetchWithCache } from '../../lib/cache';
import { BarChart3, TrendingUp, TrendingDown, Package, IndianRupee, RefreshCcw } from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination"

export default function AdminReports() {
  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [profitReport, setProfitReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderSort, setOrderSort] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [salesPage, setSalesPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [orderSearch, orderSort]);

  useEffect(() => {
    setSalesPage(1);
  }, [dateRange]);

  const fetchReports = useCallback(async (forceRefresh = false) => {
    // If refreshing, don't set main loading state to avoid flickering
    if (!forceRefresh) setLoading(true);

    try {
      // Calculate dates outside the cache function so they can be used in cache key
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - parseInt(dateRange));
      fromDate.setHours(0, 0, 0, 0);
      
      const toDate = new Date();
      toDate.setHours(23, 59, 59, 999);

      await fetchWithCache(
        `admin_reports_${dateRange}_${toDate.toDateString()}_v3`,
        async () => {
          const [salesRes, inventoryRes, profitRes] = await Promise.all([
            reportsAPI.getSales({ 
              date_from: fromDate.toISOString(),
              date_to: toDate.toISOString()
            }),
            reportsAPI.getInventory(),
            reportsAPI.getProfitLoss({ 
              date_from: fromDate.toISOString(),
              date_to: toDate.toISOString()
            }),
          ]);

          return {
            sales: salesRes.data,
            inventory: inventoryRes.data,
            profit: profitRes.data
          };
        },
        (data, isFromCache) => {
          setSalesReport(data.sales);
          setInventoryReport(data.inventory);
          setProfitReport(data.profit);
          if (isFromCache && !forceRefresh) setLoading(false);
        },
        { skipCache: forceRefresh }
      );
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReports(false);
  }, [fetchReports]);

  const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
    <Card className="bg-slate-800 border-slate-700">
      <div className="flex items-start justify-between">
        <div className={`p-2 rounded-lg bg-slate-700/50`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
      <div className="mt-4">
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-slate-400 text-sm">{title}</p>
        {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
      </div>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Reports & Analytics</h1>
          <p className="text-slate-400">Track your business performance</p>
        </div>
        <div className="flex gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40 input-admin">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="border-slate-600"
            onClick={async () => {
              setRefreshing(true);
              await fetchReports(true);
              setRefreshing(false);
            }}
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="sales">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="sales">Sales Report</TabsTrigger>
          <TabsTrigger value="inventory">Inventory Report</TabsTrigger>
          <TabsTrigger value="profit">Profit & Loss</TabsTrigger>
        </TabsList>

        {/* Sales Report */}
        <TabsContent value="sales" className="space-y-6">
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-slate-800 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : salesReport && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="Total Sales"
                  value={`₹${(salesReport.summary.total_sales || 0).toLocaleString()}`}
                  icon={IndianRupee}
                  color="text-emerald-400"
                />
                <StatCard
                  title="Total Orders"
                  value={salesReport.summary.total_orders || 0}
                  icon={Package}
                  color="text-blue-400"
                />
                <StatCard
                  title="Online Sales"
                  value={`₹${(salesReport.summary.online_sales || 0).toLocaleString()}`}
                  icon={TrendingUp}
                  color="text-primary"
                />
                <StatCard
                  title="Offline Sales"
                  value={`₹${(salesReport.summary.offline_sales || 0).toLocaleString()}`}
                  icon={BarChart3}
                  color="text-amber-400"
                />
              </div>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle>Daily Sales Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  {salesReport.daily_breakdown?.length > 0 ? (
                    <>
                      <div className="space-y-2">
                        {(() => {
                          // Sort by date descending (newest first)
                          const sortedDays = [...salesReport.daily_breakdown].sort((a, b) =>
                            new Date(b.date) - new Date(a.date)
                          );

                          const totalPages = Math.ceil(sortedDays.length / itemsPerPage);
                          const paginatedDays = sortedDays.slice(
                            (salesPage - 1) * itemsPerPage,
                            salesPage * itemsPerPage
                          );

                          return (
                            <>
                              {paginatedDays.map((day) => (
                                <div key={day.date} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                                  <span className="text-slate-400">
                                    {new Date(day.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                                  </span>
                                  <div className="text-right">
                                    <p className="font-semibold">₹{day.sales.toLocaleString()}</p>
                                    <p className="text-xs text-slate-400">{day.orders} orders</p>
                                  </div>
                                </div>
                              ))}

                              {/* Pagination Controls */}
                              {totalPages > 1 && (
                                <div className="mt-6">
                                  <Pagination>
                                    <PaginationContent>
                                      <PaginationItem>
                                        <PaginationPrevious
                                          onClick={() => setSalesPage(p => Math.max(1, p - 1))}
                                          className={salesPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                        />
                                      </PaginationItem>

                                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(page => {
                                          return page === 1 || page === totalPages || Math.abs(page - salesPage) <= 1;
                                        })
                                        .map((page, i, arr) => (
                                          <React.Fragment key={page}>
                                            {i > 0 && arr[i - 1] !== page - 1 && (
                                              <PaginationItem>
                                                <span className="px-2">...</span>
                                              </PaginationItem>
                                            )}
                                            <PaginationItem>
                                              <PaginationLink
                                                isActive={salesPage === page}
                                                onClick={() => setSalesPage(page)}
                                                className="cursor-pointer"
                                              >
                                                {page}
                                              </PaginationLink>
                                            </PaginationItem>
                                          </React.Fragment>
                                        ))}

                                      <PaginationItem>
                                        <PaginationNext
                                          onClick={() => setSalesPage(p => Math.min(totalPages, p + 1))}
                                          className={salesPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                        />
                                      </PaginationItem>
                                    </PaginationContent>
                                  </Pagination>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </>
                  ) : (
                    <p className="text-center text-slate-400 py-8">No sales data available</p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Inventory Report */}
        <TabsContent value="inventory" className="space-y-6">
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-slate-800 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : inventoryReport && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="Total Products"
                  value={inventoryReport.summary.total_products || 0}
                  icon={Package}
                  color="text-blue-400"
                />
                <StatCard
                  title="Stock Value"
                  value={`₹${(inventoryReport.summary.total_stock_value || 0).toLocaleString()}`}
                  icon={IndianRupee}
                  color="text-emerald-400"
                />
                <StatCard
                  title="Low Stock Items"
                  value={inventoryReport.summary.low_stock_count || 0}
                  icon={TrendingDown}
                  color="text-amber-400"
                />
                <StatCard
                  title="Out of Stock"
                  value={inventoryReport.summary.out_of_stock_count || 0}
                  icon={TrendingDown}
                  color="text-red-400"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle>Low Stock Products</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {inventoryReport.low_stock_products?.length > 0 ? (
                      <div className="space-y-2">
                        {inventoryReport.low_stock_products.map((product) => (
                          <div key={product.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium truncate max-w-[200px]">{product.name}</p>
                              <p className="text-xs text-slate-400">{product.sku}</p>
                            </div>
                            <div className="flex gap-4 items-center">
                              <div className="text-right">
                                <p className="text-xs text-slate-400">Sold</p>
                                <span className="text-blue-400 font-semibold">{product.sold_qty || 0}</span>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-slate-400">Stock</p>
                                <span className="text-amber-400 font-semibold">{product.stock_qty}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-slate-400 py-4">No low stock items</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle>Out of Stock Products</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {inventoryReport.out_of_stock_products?.length > 0 ? (
                      <div className="space-y-2">
                        {inventoryReport.out_of_stock_products.map((product) => (
                          <div key={product.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium truncate max-w-[200px]">{product.name}</p>
                              <p className="text-xs text-slate-400">{product.sku}</p>
                            </div>
                            <div className="flex gap-4 items-center">
                              <div className="text-right">
                                <p className="text-xs text-slate-400">Sold</p>
                                <span className="text-blue-400 font-semibold">{product.sold_qty || 0}</span>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-slate-400">Stock</p>
                                <span className="text-red-400 font-semibold">0</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-slate-400 py-4">No out of stock items</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Profit & Loss Report */}
        <TabsContent value="profit" className="space-y-6">
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-slate-800 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : profitReport && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <StatCard
                  title="Total Revenue"
                  value={`₹${(profitReport.summary.total_revenue || 0).toLocaleString()}`}
                  icon={IndianRupee}
                  color="text-emerald-400"
                />
                <StatCard
                  title="Actual Cost"
                  value={`₹${(profitReport.summary.adjusted_cost || 0).toLocaleString()}`}
                  icon={TrendingDown}
                  color="text-red-400"
                  subtext={`${profitReport.total_qty_sold || 0} items sold`}
                />
                <StatCard
                  title="Net Profit"
                  value={`₹${(profitReport.summary.net_profit || 0).toLocaleString()}`}
                  icon={TrendingUp}
                  color={profitReport.summary.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}
                  subtext={`${(profitReport.summary.profit_margin || 0).toFixed(1)}% margin`}
                />
              </div>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle>Profit Breakdown (Corrected Calculation)</CardTitle>
                  <p className="text-sm text-slate-400">Simple profit calculation: Revenue minus Cost of Goods Sold</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                      <span>Total Revenue</span>
                      <span className="font-bold text-emerald-400">₹{(profitReport.summary.total_revenue || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                      <span>Cost of Goods Sold</span>
                      <span className="font-bold text-red-400">₹{(profitReport.summary.total_cost || 0).toLocaleString()}</span>
                    </div>
                    {/* <div className="border-t border-slate-600 pt-4">
                      <div className="flex justify-between items-center p-4 bg-emerald-500/20 rounded-lg border border-emerald-500/30">
                        <span className="font-semibold">Gross Profit (Revenue - Cost)</span>
                        <span className={`font-bold text-lg ${profitReport.summary.gross_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          ₹{(profitReport.summary.gross_profit || 0).toLocaleString()}
                        </span>
                      </div>
                    </div> */}
                    {/* <div className="border-t border-slate-600 pt-4">
                      <div className="flex justify-between items-center p-4 bg-emerald-500/20 rounded-lg border border-emerald-500/30">
                        <span className="font-semibold">Net Profit (Same as Gross Profit)</span>
                        <span className={`font-bold text-lg ${profitReport.summary.gross_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          ₹{(profitReport.summary.gross_profit || 0).toLocaleString()}
                        </span>
                      </div>
                    </div> */}
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="flex justify-between items-center p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        <span>Orders Completed</span>
                        <span className="font-bold text-blue-400">{profitReport.orders_count || 0}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg border border-primary/20">
                        <span>Profit Margin</span>
                        <span className="font-bold text-primary">{(profitReport.summary.profit_margin || 0).toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Order-wise Profit Analysis */}
              {profitReport.order_wise_profits && profitReport.order_wise_profits.length > 0 && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle>Complete Order-wise Profit Analysis</CardTitle>
                    <div className="flex flex-col md:flex-row gap-4 mt-4 justify-between items-end">
                      <p className="text-sm text-slate-400 mb-2 md:mb-0">
                        All {profitReport.total_profitable_orders || 0} orders in selected time period
                      </p>
                      <div className="flex gap-2 w-full md:w-auto">
                        <div className="relative w-full md:w-64">
                          <Input
                            placeholder="Search Order #"
                            value={orderSearch}
                            onChange={(e) => setOrderSearch(e.target.value)}
                            className="bg-slate-900 border-slate-700 w-full"
                          />
                        </div>
                        <Select value={orderSort} onValueChange={setOrderSort}>
                          <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700">
                            <SelectValue placeholder="Sort by Date" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="desc">Date: Newest First</SelectItem>
                            <SelectItem value="asc">Date: Oldest First</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-600">
                            <th className="text-left p-2">Order #</th>
                            <th className="text-left p-2">Date</th>
                            <th className="text-left p-2">Status</th>
                            <th className="text-right p-2">Revenue</th>
                            <th className="text-right p-2" title="Refunds">Refund</th>
                            <th className="text-right p-2" title="Net Revenue">Net Rev</th>
                            <th className="text-right p-2">Cost</th>
                            <th className="text-right p-2">Profit</th>
                            <th className="text-right p-2">Margin</th>
                            <th className="text-right p-2">Delivery</th>
                            <th className="text-right p-2">Packing</th>
                            <th className="text-center p-2">Items</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const filteredOrders = profitReport.order_wise_profits
                              .filter(order => order.order_number.toLowerCase().includes(orderSearch.toLowerCase()))
                              .sort((a, b) => {
                                const dateA = new Date(a.date);
                                const dateB = new Date(b.date);
                                return orderSort === 'asc' ? dateA - dateB : dateB - dateA;
                              });

                            const paginatedOrders = filteredOrders.slice(
                              (currentPage - 1) * itemsPerPage,
                              currentPage * itemsPerPage
                            );

                            return (
                              <>
                                {paginatedOrders.map((order, index) => (
                                  <tr key={order.order_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                                    <td className="p-2 font-mono text-xs">{order.order_number}</td>
                                    <td className="p-2 text-xs text-slate-400">
                                      {new Date(order.date).toLocaleDateString('en-IN')}
                                    </td>
                                    <td className="p-2">
                                      <span className={`px-2 py-1 rounded text-xs capitalize ${order.status === 'returned' ? 'bg-red-500/10 text-red-500' :
                                          order.status === 'partial_returned' ? 'bg-orange-500/10 text-orange-500' :
                                            order.status === 'cancelled' ? 'bg-red-500/10 text-red-500' :
                                              'bg-green-500/10 text-green-500'
                                        }`}>
                                        {order.status || 'completed'}
                                      </span>
                                    </td>
                                    <td className="p-2 text-right font-semibold text-emerald-400">
                                      ₹{Math.round(order.revenue)}
                                    </td>
                                    <td className={`p-2 text-right ${order.refund > 0 ? "text-red-400" : "text-slate-500"}`}>
                                      {order.refund > 0 ? `-₹${Math.round(order.refund)}` : '-'}
                                    </td>
                                    <td className="p-2 text-right">
                                      ₹{Math.round(order.net_revenue || (order.revenue - (order.refund || 0)))}
                                    </td>
                                    <td className="p-2 text-right text-red-400">
                                      ₹{Math.round(order.cost - (order.returned_cost || 0))}
                                    </td>
                                    <td className={`p-2 text-right font-bold ${order.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                      ₹{Math.round(order.profit)}
                                    </td>
                                    <td className={`p-2 text-right text-xs ${order.profit_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                      {order.profit_margin.toFixed(1)}%
                                    </td>
                                    <td className="p-2 text-right text-xs text-slate-400">
                                      {order.delivery_charges > 0 ? `₹${order.delivery_charges.toLocaleString()}` : 'FREE'}
                                    </td>
                                    <td className="p-2 text-right text-xs text-slate-400">
                                      {order.packing_charges > 0 ? `₹${order.packing_charges.toLocaleString()}` : 'FREE'}
                                    </td>
                                    <td className="p-2 text-center text-xs text-slate-400">
                                      {order.items_count}
                                    </td>
                                  </tr>
                                ))}
                                {paginatedOrders.length === 0 && (
                                  <tr>
                                    <td colSpan={9} className="text-center p-4 text-slate-400">No orders found</td>
                                  </tr>
                                )}
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {profitReport.order_wise_profits.length > itemsPerPage && (
                      <div className="mt-4">
                        {(() => {
                          const filteredCount = profitReport.order_wise_profits
                            .filter(order => order.order_number.toLowerCase().includes(orderSearch.toLowerCase()))
                            .length;
                          const totalPages = Math.ceil(filteredCount / itemsPerPage);

                          if (totalPages <= 1) return null;

                          return (
                            <Pagination>
                              <PaginationContent>
                                <PaginationItem>
                                  <PaginationPrevious
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                  />
                                </PaginationItem>

                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                  .filter(page => {
                                    // Show first, last, and pages around current
                                    return page === 1 ||
                                      page === totalPages ||
                                      Math.abs(page - currentPage) <= 1;
                                  })
                                  .map((page, i, arr) => (
                                    <React.Fragment key={page}>
                                      {i > 0 && arr[i - 1] !== page - 1 && (
                                        <PaginationItem>
                                          <span className="px-2">...</span>
                                        </PaginationItem>
                                      )}
                                      <PaginationItem>
                                        <PaginationLink
                                          isActive={currentPage === page}
                                          onClick={() => setCurrentPage(page)}
                                          className="cursor-pointer"
                                        >
                                          {page}
                                        </PaginationLink>
                                      </PaginationItem>
                                    </React.Fragment>
                                  ))}

                                <PaginationItem>
                                  <PaginationNext
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                  />
                                </PaginationItem>
                              </PaginationContent>
                            </Pagination>
                          );
                        })()}
                      </div>
                    )}

                    {/* Summary Statistics */}
                    {profitReport.order_wise_profits && profitReport.order_wise_profits.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-600">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div className="bg-slate-700/30 rounded-lg p-3">
                            <p className="text-lg font-bold text-emerald-400">
                              ₹{profitReport.order_wise_profits.reduce((sum, order) => sum + order.profit, 0).toLocaleString()}
                            </p>
                            <p className="text-xs text-slate-400">Total Profit (All Orders)</p>
                          </div>
                          <div className="bg-slate-700/30 rounded-lg p-3">
                            <p className="text-lg font-bold text-blue-400">
                              {(profitReport.order_wise_profits.reduce((sum, order) => sum + order.profit_margin, 0) / profitReport.order_wise_profits.length).toFixed(1)}%
                            </p>
                            <p className="text-xs text-slate-400">Avg Profit Margin</p>
                          </div>
                          <div className="bg-slate-700/30 rounded-lg p-3">
                            <p className="text-lg font-bold text-primary">
                              ₹{Math.max(...profitReport.order_wise_profits.map(order => order.profit)).toLocaleString()}
                            </p>
                            <p className="text-xs text-slate-400">Highest Profit Order</p>
                          </div>
                          <div className="bg-slate-700/30 rounded-lg p-3">
                            <p className="text-lg font-bold text-amber-400">
                              ₹{(profitReport.order_wise_profits.reduce((sum, order) => sum + order.revenue, 0) / profitReport.order_wise_profits.length).toLocaleString()}
                            </p>
                            <p className="text-xs text-slate-400">Avg Order Value</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
